/**
 * File: GenerationDashboard.tsx
 * Description: Main dashboard component for architecture generation with real-time monitoring
 * Author: System
 * Created: 2025-08-09
 * Last Modified: 2025-08-09
 */

import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ShareIcon,
  EyeIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import { AIDisclaimer } from '../../components/AIDisclaimer';
import { STAGE_PAGE_WIDTH_CLASS } from '../../constants/layout';
import {
  GenerationStatus,
  GenerationDashboardProps,
} from '../../types/generation';
import { InviteCollaboratorsModal } from '../InviteCollaboratorsModal';
import { PageHeader } from '../PageHeader';

import { ActivityLogger } from './ActivityLogger';
import { SectionPreview } from './SectionPreview';
/**
 * Main dashboard for managing architecture generation workflow
 */
export const GenerationDashboard: React.FC<GenerationDashboardProps> = ({
  projectContext,
  generationState,
  logs,
  sectionsGenerating,
  pendingGroundedness,
  selectedSection,
  onStartGeneration,
  onPauseGeneration,
  onResumeGeneration,
  onCancelGeneration,
  onGenerateSection,
  onUpdateSection,
  onSectionSelect,
}) => {
  const [showActivityLog, setShowActivityLog] = useState<boolean>(false);
  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);

  // Real-time updates
  useEffect(() => {
    // Set up WebSocket or polling for real-time updates
    // This would connect to the backend for live generation status
  }, []);

  // Auto-select first section when sections are available
  useEffect(() => {
    if (!selectedSection && Object.keys(generationState.sections).length > 0 && onSectionSelect) {
      const firstSectionKey = Object.keys(generationState.sections)[0];
      onSectionSelect(firstSectionKey);
    }
  }, [generationState.sections, selectedSection, onSectionSelect]);

  const handleToggleActivityLog = () => {
    setShowActivityLog((previous) => !previous);
  };

  const getStatusColor = (status: GenerationStatus): string => {
    switch (status) {
      case GenerationStatus.RUNNING:
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case GenerationStatus.COMPLETED:
        return 'text-green-600 bg-green-50 border-green-200';
      case GenerationStatus.PAUSED:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case GenerationStatus.ERROR:
        return 'text-red-600 bg-red-50 border-red-200';
      case GenerationStatus.CANCELLED:
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: GenerationStatus) => {
    switch (status) {
      case GenerationStatus.RUNNING:
        return <PlayIcon className="w-4 h-4 text-blue-600" />;
      case GenerationStatus.COMPLETED:
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case GenerationStatus.PAUSED:
        return <PauseIcon className="w-4 h-4 text-yellow-600" />;
      case GenerationStatus.ERROR:
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      case GenerationStatus.CANCELLED:
        return <StopIcon className="w-4 h-4 text-gray-600" />;
      default:
        return <ClockIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const isGenerationActive = [GenerationStatus.RUNNING, GenerationStatus.PAUSED].includes(generationState.status);
  const canStart = generationState.status === GenerationStatus.IDLE;
  const canPause = generationState.status === GenerationStatus.RUNNING && generationState.canPause;
  const canResume = generationState.status === GenerationStatus.PAUSED && generationState.canResume;
  const canCancel = isGenerationActive && generationState.canCancel;

  return (
    <div className="min-h-screen bg-background-base">
      <div className={`${STAGE_PAGE_WIDTH_CLASS} py-8`}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <PageHeader
              title="Architecture Analysis"
              subtitle={`Generate comprehensive architecture documentation for ${projectContext.projectTitle}`}
              icon={DocumentTextIcon}
              iconBgColor="#10B981"
              // This page provides its own Share button in the header row.
              showShareAction={false}
            />
            {/* Share (swapped up here from the control bar) */}
            <button
              onClick={() => setIsShareOpen(true)}
              className="inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-lg border border-border-medium bg-background-surface font-medium text-text-onSurface-strong transition-colors hover:bg-background-section"
            >
              <ShareIcon className="w-4 h-4" />
              Share
            </button>
          </div>
          {/* AI Disclaimer */}
          <AIDisclaimer className="mb-6" />
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-background-surface border border-border-subtle rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <DocumentTextIcon className="w-4 h-4 text-text-onSurface-subtle" />
                <span className="text-sm text-text-onSurface-subtle">Sections</span>
              </div>
              <div className="text-lg font-semibold text-text-onSurface-strong">
                {generationState.progress.completedSections} / {generationState.progress.totalSections}
              </div>
            </div>

            <div className="bg-background-surface border border-border-subtle rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <ChartBarIcon className="w-4 h-4 text-text-onSurface-subtle" />
                <span className="text-sm text-text-onSurface-subtle">Progress</span>
              </div>
              <div className="text-lg font-semibold text-text-onSurface-strong">
                {generationState.progress.overallPercentage}%
              </div>
            </div>

            <div className="group relative bg-background-surface border border-border-subtle rounded-lg p-4 cursor-help">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircleIcon className="w-4 h-4 text-text-onSurface-subtle" />
                <span className="text-sm text-text-onSurface-subtle">Groundedness Score</span>
              </div>
              <div className="text-lg font-semibold text-text-onSurface-strong">
                {generationState.overallQuality?.groundednessScore?.toFixed(0) || '--'}%
              </div>

              {/* Tooltip */}
              <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-max max-w-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none group-hover:pointer-events-auto z-50 hidden group-hover:block">
                <div className="bg-gray-900 text-white text-sm rounded-lg p-4 shadow-lg space-y-2">
                  <div className="font-semibold mb-2">Scoring Guidelines</div>
                  <div>
                    <span className="font-medium">0-30%:</span> Completely hallucinated or unsupported claims
                  </div>
                  <div>
                    <span className="font-medium">30-50%:</span> Significant hallucinations or unsupported claims
                  </div>
                  <div>
                    <span className="font-medium">51-70%:</span> Some unsupported claims but majority is grounded
                  </div>
                  <div>
                    <span className="font-medium">71-85%:</span> Mostly grounded with minor issues
                  </div>
                  <div>
                    <span className="font-medium">86-100%:</span> Fully or nearly fully grounded in source material
                  </div>
                  {/* Tooltip arrow */}
                  <div className="absolute left-1/2 top-full -translate-x-1/2 -translate-y-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Control Bar */}
        <div className="bg-background-surface border border-border-subtle rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {canStart && (
                <button
                  onClick={onStartGeneration}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-tertiary-strong text-white rounded-lg hover:bg-accent-tertiary-medium transition-colors main-button"
                >
                  <PlayIcon className="w-4 h-4" />
                  Start Generation
                </button>
              )}

              {canPause && (
                <button
                  onClick={onPauseGeneration}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors main-button"
                >
                  <PauseIcon className="w-4 h-4" />
                  Pause
                </button>
              )}

              {canResume && (
                <button
                  onClick={onResumeGeneration}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors main-button"
                >
                  <PlayIcon className="w-4 h-4" />
                  Resume
                </button>
              )}

              {canCancel && (
                <button
                  onClick={onCancelGeneration}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors main-button"
                >
                  <StopIcon className="w-4 h-4" />
                  Cancel
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Status Badge (swapped down here from the header) */}
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border font-medium ${
                getStatusColor(generationState.status)
              }`}>
                {getStatusIcon(generationState.status)}
                <span className="capitalize">{generationState.status.replace('_', ' ')}</span>
              </div>

              <button
                onClick={handleToggleActivityLog}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  showActivityLog 
                    ? 'bg-accent-tertiary-strong text-white border-accent-tertiary-strong' 
                    : 'bg-background-surface text-text-onSurface-strong border-border-medium ' +
                      'hover:bg-background-section'
                }`}
              >
                <EyeIcon className="w-4 h-4" />
                Activity Log
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Main Content Area */}
          <div className="grid grid-cols-12 gap-6">
            {/* Left Panel - Section Preview */}
            <div className={`${showActivityLog ? 'col-span-8' : 'col-span-12'}`}>
              <div className="bg-background-surface border border-border-subtle rounded-lg">
                <div className="p-6">
                  <SectionPreview
                    sections={generationState.sections}
                    selectedSection={selectedSection || null}
                    sectionsGenerating={sectionsGenerating}
                    pendingGroundedness={pendingGroundedness}
                    configuredSections={generationState.config.selectedSections}
                    projectId={Number.parseInt(projectContext.projectId, 10) || -1}
                    projectContext={projectContext}
                    onSectionSelect={onSectionSelect || (() => { /* Default handler */ })}
                    onGenerateSection={onGenerateSection}
                    onUpdateSection={onUpdateSection}
                  />
                </div>
              </div>
            </div>

            {/* Right Panel - Activity Log */}
            {showActivityLog && (
              <div className="col-span-4">
                <div className="h-[600px]">
                  <ActivityLogger
                    logs={logs}
                    isLive={isGenerationActive}
                    onClearLogs={() => {/* Handle clear logs */}}
                    onExportLogs={() => {/* Handle export logs */}}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isShareOpen && (
        <InviteCollaboratorsModal
          isOpen
          onClose={() => setIsShareOpen(false)}
          projectId={Number.parseInt(projectContext.projectId, 10) || -1}
          projectTitle={projectContext.projectTitle}
        />
      )}
    </div>
  );
};
